import { computed, ref, watch } from 'vue';
import { useStoreSsPaymentMethods } from '~/stores/softswiss/paymentMethods';
import { useStoreSsWithdrawMethods } from '~/stores/softswiss/withdrawMethods';
import { ECryptoMethodTypes } from '~/types/cryptoMethodTypes';
import type { MethodMultiProtocolCryptoAddressField } from '~/types/paymentsApi/types';

export const useCryptoMethodCommon = (storeType: 'deposit' | 'withdraw') => {
  const storeMethods =
    storeType === 'deposit' ? useStoreSsPaymentMethods() : useStoreSsWithdrawMethods();
  const selectedProtocol = ref('');

  const multiProtocolCryptoMethod = computed(
    (): MethodMultiProtocolCryptoAddressField | undefined => {
      return storeMethods.paymentPlan?.methodFields.find(
        (item) => item.type === 'multi_protocol_crypto_address'
      ) as MethodMultiProtocolCryptoAddressField;
    }
  );

  const isMultiProtocolCryptoAddress = computed(() => !!multiProtocolCryptoMethod.value);

  const protocols = computed(() => multiProtocolCryptoMethod.value?.protocols || []);

  const addressByProtocol = computed(() => {
    if (isMultiProtocolCryptoAddress.value && selectedProtocol.value) {
      return multiProtocolCryptoMethod.value?.addressByProtocol[selectedProtocol.value];
    }
    return null;
  });

  const address = computed(() => {
    if (isMultiProtocolCryptoAddress.value) {
      return addressByProtocol.value?.address;
    }

    const method = storeMethods.paymentPlan?.methodFields.find(
      (item) =>
        item.type === 'deposit_crypto_address' && item.fieldName === ECryptoMethodTypes.address
    );

    // @ts-ignore
    return method?.address;
  });

  const tag = computed(() => {
    const method = storeMethods.paymentPlan?.methodFields.find(
      (item) => item.type === 'deposit_crypto_address' && item.fieldName === ECryptoMethodTypes.tag
    );

    // @ts-ignore
    return method?.address;
  });

  const walletQr = computed((): string => {
    if (isMultiProtocolCryptoAddress.value) {
      return addressByProtocol.value?.href || '';
    }

    const method = storeMethods.paymentPlan?.methodFields.find(
      (item) =>
        item.type === 'deposit_crypto_address' && item.fieldName === ECryptoMethodTypes.address
    );

    // @ts-ignore
    return method?.href;
  });

  watch(
    () => isMultiProtocolCryptoAddress.value,
    () => {
      if (isMultiProtocolCryptoAddress.value) {
        selectedProtocol.value = multiProtocolCryptoMethod.value?.proposedValue || '';
      }
    },
    { immediate: true }
  );

  return {
    selectedProtocol,
    isMultiProtocolCryptoAddress,
    multiProtocolCryptoMethod,
    protocols,
    address,
    tag,
    walletQr,
  };
};
